const RESULT_MIME_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const readBlobBytes = async (blob: Blob): Promise<Uint8Array> => {
  if (typeof blob.arrayBuffer === "function") {
    return new Uint8Array(await blob.arrayBuffer());
  }
  return new Promise<Uint8Array>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(new Error("No fue posible leer el archivo generado."));
    reader.readAsArrayBuffer(blob);
  });
};

const bytesFromBase64 = (value: string): Uint8Array => {
  const decoded = atob(value.replace(/\s+/g, ""));
  const bytes = new Uint8Array(decoded.length);
  for (let index = 0; index < decoded.length; index += 1) bytes[index] = decoded.charCodeAt(index);
  return bytes;
};

const textFromBytes = (bytes: Uint8Array): string => {
  if (typeof TextDecoder !== "undefined") return new TextDecoder().decode(bytes);
  let text = "";
  const chunkSize = 8192;
  for (let index = 0; index < bytes.length; index += chunkSize) {
    text += String.fromCharCode(...Array.from(bytes.slice(index, index + chunkSize)));
  }
  return text;
};

const bytesFromUnknown = async (value: unknown): Promise<Uint8Array> => {
  if (value instanceof Blob) {
    const bytes = await readBlobBytes(value);
    let firstContentByte = 0;
    while ([0x09, 0x0a, 0x0d, 0x20].includes(bytes[firstContentByte])) firstContentByte += 1;
    if ([0x22, 0x5b, 0x7b].includes(bytes[firstContentByte])) {
      try {
        return bytesFromUnknown(JSON.parse(textFromBytes(bytes).trim()));
      } catch (_) {
        return bytes;
      }
    }
    return bytes;
  }
  if (value instanceof ArrayBuffer) return new Uint8Array(value);
  if (ArrayBuffer.isView(value)) {
    return new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
  }
  if (Array.isArray(value)) return Uint8Array.from(value);
  if (value && typeof value === "object") {
    const wrapped = value as { body?: unknown; data?: unknown };
    if (Array.isArray(wrapped.data)) return Uint8Array.from(wrapped.data);
    if (wrapped.body !== undefined) return bytesFromUnknown(wrapped.body);
    if (wrapped.data !== undefined) return bytesFromUnknown(wrapped.data);
  }
  if (typeof value === "string") {
    const text = value.trim();
    if (text.startsWith("{") || text.startsWith("[")) {
      try {
        return bytesFromUnknown(JSON.parse(text));
      } catch (_) {
        // Continúa para admitir respuestas binarias representadas como texto.
      }
    }
    const dataUri = /^data:[^;]+;base64,(.+)$/i.exec(text);
    if (dataUri?.[1]) return bytesFromBase64(dataUri[1]);
    if (/^[A-Za-z0-9+/\s]+={0,2}$/.test(text) && text.replace(/\s+/g, "").length % 4 === 0) {
      try {
        const decoded = bytesFromBase64(text);
        if (decoded[0] === 0x50 && decoded[1] === 0x4b) return decoded;
      } catch (_) {
        // Se intenta como texto binario en el siguiente paso.
      }
    }
    return Uint8Array.from(text, (character) => character.charCodeAt(0) & 0xff);
  }
  throw new Error("La respuesta del servidor no contiene un archivo reconocible.");
};

export const normalizePortalPayrollBlob = async (value: unknown): Promise<Blob> => {
  const bytes = await bytesFromUnknown(value);
  if (bytes.length < 4 || bytes[0] !== 0x50 || bytes[1] !== 0x4b) {
    throw new Error("La respuesta del servidor no contiene un Excel válido.");
  }
  const exactBuffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
  return new Blob([exactBuffer], { type: RESULT_MIME_TYPE });
};

export const getPortalPayrollHeader = (
  headers: Record<string, unknown>,
  expectedName: string
): unknown => {
  const expected = expectedName.toLowerCase();
  const actualName = Object.keys(headers ?? {}).find((name) => name.toLowerCase() === expected);
  return actualName ? headers[actualName] : undefined;
};
